import React, { Fragment, useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import {
  Box,
  Grid2,
  Typography,
  TextField,
  Paper,
  useTheme,
  Card,
  alpha,
  Tooltip,
} from "@mui/material";
import dayjs from "dayjs";
import theme from "../Theme";
import { getServices } from "../API/LinkedServicesApi";
import AuthContext from "../Context/AuthProvider";
import { useSnackbar } from "../Utility/UserMessageContext";
import { convertDateTimeToFacilityTimeZone, convertToUTC, convertUTCToTimeZone } from "../Utility/DateTimeUtility";
import { getWorkingHours } from "../API/UserSettingsApi";

const SchedulingAssistant = ({ physician, selectedDate, newSlot }) => {
  const [workingHours, setWorkingHours] = useState([]);
  const { showSnackbar } = useSnackbar();
  const {auth} = useContext(AuthContext);
  const [scheduledServices, setScheduledServices] = useState([]);
  //const [selectedDate, setSelectedDate] = useState(new Date());
  const gridRef = useRef();
  //const [timeSlots, setTimeSlots] = useState([]);
  
      const timeSlots = () => {
        const slots = [];
        for (let hour = 0; hour < 24; hour++) {
          for (let half = 0; half < 2; half++) {
            const time = `${hour.toString().padStart(2, "0")}:${
              half === 0 ? "00" : "30"
            }`;
            slots.push(time);
          }
        }
        return slots;

      };
      
      
  useEffect(() => {
    const scrollToTime = () => {
      if (gridRef.current) {
        // Access the current property of the ref
        const scrollToTime = "09:00"; // Example time to scroll to
        const [hour, minute] = scrollToTime.split(":").map(Number);
        const scrollPosition = ((hour * 60 + minute) / 30) * 40; // Calculate scroll position
        gridRef.current.scrollTop = scrollPosition; // Use gridRef.current
      }
    };
    scrollToTime();
  }, [gridRef, selectedDate, physician]);

  useEffect(() => {
    const loadWorkingHours = async () => {
      if(auth && physician && selectedDate) {
      const result = await getWorkingHours(auth, physician?.id, selectedDate);
      if (result?.isSuccess) {
        const workingHours = [];
        result?.data?.map((wh) => {
          const startTime = convertDateTimeToFacilityTimeZone(dayjs().set("hour", wh?.startTime?.split(":")[0]).set("minute", wh?.startTime?.split(":")[1]), auth?.auth?.selectedFacility?.timeZone);
          const endTime = convertDateTimeToFacilityTimeZone(dayjs().set("hour", wh?.endTime?.split(":")[0]).set("minute", wh?.endTime?.split(":")[1]), auth?.auth?.selectedFacility?.timeZone);
          const item = {
            from: dayjs().set("hour", dayjs(startTime).hour()).set("minute", dayjs(startTime).minute()),
            to: dayjs().set("hour", dayjs(endTime).hour()).set("minute", dayjs(endTime).minute()),
          };
          workingHours.push(item);
        });
        setWorkingHours(workingHours);
        
      } else {
        setWorkingHours([]);
        showSnackbar("Error occurred while loading the working hours.", "error");
      }
    }

    }
    const loadScheduledServices = async () => {

      const filterCriteria = {
        physicianId: physician?.id,
        from: dayjs(convertToUTC(selectedDate)),
        to: dayjs(convertToUTC(selectedDate?.add(1, "day"))),
      };
      const result = await getServices(
        auth, filterCriteria
      );
      if (result.isSuccess) {
        const services = [];
        result?.data?.map((service) => {
          const localStartDateTime = convertUTCToTimeZone(service?.scheduledStartDateTime, auth?.auth?.selectedFacility?.timeZone);
          const localEndDateTime = convertUTCToTimeZone(service?.scheduledEndDateTime, auth?.auth?.selectedFacility?.timeZone);
          const onwardTravelTime = service?.onwardTravelTime ? service?.onwardTravelTime : 0;
          const returnTravelTime = service?.returnTravelTime ? service?.returnTravelTime : 0;
          const item = {
          id: service?.id,
          onwardTravelTime: service?.onwardTravelTime,
          returnTravelTime: service?.returnTravelTime,
          from: dayjs().set("hour", dayjs(localStartDateTime).hour()).set("minute", dayjs(localStartDateTime).minute()),
          to: dayjs().set("hour", dayjs(localEndDateTime).hour()).set("minute", dayjs(localEndDateTime).minute()),
          fromWithTravel: dayjs().set("hour", dayjs(localStartDateTime).hour()).set("minute", dayjs(localStartDateTime).minute()).subtract(onwardTravelTime, "minute"),
          toWithTravel: dayjs().set("hour", dayjs(localEndDateTime).hour()).set("minute", dayjs(localEndDateTime).minute()).add(returnTravelTime, "minute"),
          title: `${service?.patientFirstName} ${service?.patientLastName}, ${service?.serviceName}`,
          new:false,
          };
          services.push(item);
        });
        setScheduledServices(services);
      } else {
        setScheduledServices([]);
        showSnackbar("Error occurred while loading the scheduled services in scheduling assistant.", "error");
      }
    };
    loadScheduledServices();
    loadWorkingHours();
    renderAppointments();
  }, [gridRef, selectedDate, physician, auth]);

  useEffect(() => {
    renderAppointments();
  }, [newSlot]);

  const renderAppointments = () => {
    //if (!scheduledServices || scheduledServices.length === 0) return null;
    var servicesWithNewSlot = scheduledServices;
    if(newSlot && newSlot?.from && newSlot?.to && newSlot?.from?.isValid() && newSlot?.to?.isValid()) 
    {
      const newSlotWithTravelTime = newSlot;
      const onwardTravelTime = newSlot?.onwardTravelTime ? newSlot?.onwardTravelTime : 0;
      const returnTravelTime = newSlot?.returnTravelTime ? newSlot?.returnTravelTime : 0;
      newSlotWithTravelTime.fromWithTravel = dayjs(newSlot?.from).subtract(onwardTravelTime, "minute");
      newSlotWithTravelTime.toWithTravel = dayjs(newSlot?.to).add(returnTravelTime, "minute");
      servicesWithNewSlot = [...scheduledServices, newSlot];
    }
    // Step 1: Sort services by start time
    const sortedServices = [...servicesWithNewSlot].sort((a, b) =>
      a.fromWithTravel?.diff(b.fromWithTravel)
    );

    
    const itemWidthArray = [];

    // Step 2: Track overlapping appointments
    const overlapCountArray = []; 
    sortedServices.forEach((service, index) => {
      var overlapCount = 1;
      sortedServices.forEach((otherService, otherIndex) => {
        if (
          index !== otherIndex &&
          service.fromWithTravel?.isBefore(otherService.toWithTravel) &&
          service.toWithTravel?.isAfter(otherService.fromWithTravel)
        ) {
          overlapCount++;
        }
      });
      overlapCountArray[index] = overlapCount;
    });

    const isOverlap = (startTime1, endTime1, startTime2, endTime2) => {
      return (
        (startTime1?.isBefore(endTime2) && endTime1?.isAfter(startTime2)) ||
        (startTime2?.isBefore(endTime1) && endTime2?.isAfter(startTime1))
      );
    }

    //const subArraysOfParallelItems = [];
    
    const displayPositions = [];
    let displayPosition = 0;
    // Step 1: Divide the array into subarrays
    for (let j = 0; j < sortedServices.length; j++) {
      const subArraryForOverlapCount = overlapCountArray.slice(
        j,
        j + overlapCountArray[j]
      );
      let noOfParallelBlocks = 0;
      subArraryForOverlapCount.map((overlapCount, index) => {
        if(index === 0) {
          noOfParallelBlocks = 1;
          displayPosition = 0;
          displayPositions[j + index] = displayPosition;
        }
        else {
          if(isOverlap(
            sortedServices[index].fromWithTravel,
            sortedServices[index].toWithTravel,
            sortedServices[index - 1].fromWithTravel,
            sortedServices[index - 1].toWithTravel
          )) {
            noOfParallelBlocks++;
            displayPosition++;
            //itemWidthArray[j + index] = 100 / noOfParallelBlocks;
            displayPositions[j + index] = displayPosition;
          }
          else
          {
            displayPositions[j + index] = displayPosition;
          }
        }
      });
      subArraryForOverlapCount.map((overlapCount, index) => {
        if (itemWidthArray[j + index] === undefined) {
          itemWidthArray[j + index] = 100 / noOfParallelBlocks;
        }
      });
      j += overlapCountArray[j] - 1;
    }

    // Step 3: Render appointments with calculated positions
    return sortedServices.map((service, index) => {
      const onwardTravelTime = service?.onwardTravelTime ? service?.onwardTravelTime : 0;
      const returnTravelTime = service?.returnTravelTime ? service?.returnTravelTime : 0;
      const onwardTravelStartTime = dayjs(service?.from).subtract(onwardTravelTime, "minute");
      //const onwardTravelEndTime = dayjs(newSlot?.from).add(onwardTravelTime, "minute");
      const returnTravelStartTime = dayjs(service?.to);
      //const returnTravelEndTime = dayjs(newSlot?.to).add(returnTravelTime, "minute");
      const visitStartTime = service.from;
      const visitEndTime = service.to;

      const startOffsetOnwardTravel = (onwardTravelStartTime?.hour() * 60 + onwardTravelStartTime?.minute()) / 30;
      const durationOnwardTravel = onwardTravelTime / 30; // Use dayjs's diff method
      // Calculate the start offset and duration
      const startOffsetVisit = (visitStartTime?.hour() * 60 + visitStartTime?.minute()) / 30;
      const durationVisit = visitEndTime?.diff(visitStartTime, "minute") / 30; // Use dayjs's diff method
      const startOffsetReturnTravel = (returnTravelStartTime?.hour() * 60 + returnTravelStartTime?.minute()) / 30;
      const durationReturnTravel = returnTravelTime / 30; // Use dayjs's diff method

      
      // Calculate the number of overlapping appointments
      const overlapCount = overlapCountArray[index]; // Include the current appointment
      const positionIndex = displayPositions[index]; // Position index for this box

      // Calculate width and left position
      //const boxWidth = 85 / overlapCount; // Divide the width equally
      const boxWidth = (85 * (itemWidthArray[index]/100));
      const boxLeft = positionIndex * boxWidth;
      const boxColor = service.new ? "secondary" : "primary";

      return (
        <Fragment>
          {onwardTravelTime > 0 && (
          <Tooltip
            key={index}
            title={
              <Box>
                <Typography variant="caption">
                    Travel Time (Onward): {service?.onwardTravelTime} mins
                </Typography>
              </Box>
            }
            placement="top"
            arrow
          >
            <Grid2
              key={index + "onwardTravel"}
              sx={{
                position: "absolute",
                top: `${startOffsetOnwardTravel * 40}px`,
                height: `${durationOnwardTravel * 40}px`,
                left: `${15 + boxLeft}%`,
                width: `${boxWidth - 1}%`,
                backgroundColor: (theme) =>
                  alpha(theme.palette[boxColor].main, 0.2),

                color: "white",
                borderColor: `${boxColor}.main`,
                borderStyle: "solid",
                borderWidth: "1px",
                borderRadius: "4px",
                padding: "4px",
                boxSizing: "border-box",
              }}
            >
            </Grid2>
          </Tooltip>
          )}
          <Tooltip
            key={index}
            title={
              <Box>
                <Typography variant="caption">
                  {service.title} ({service.from?.format("HH:mm")}-{" "}
                  {service.to?.format("HH:mm")})
                </Typography>
              </Box>
            }
            placement="top"
            arrow
          >
            <Grid2
              key={index}
              sx={{
                position: "absolute",
                top: `${startOffsetVisit * 40}px`,
                height: `${durationVisit * 40}px`,
                left: `${15 + boxLeft}%`,
                width: `${boxWidth - 1}%`,
                backgroundColor: (theme) =>
                  alpha(theme.palette[boxColor].main, 0.6),

                color: "white",
                borderColor: `${boxColor}.main`,
                borderStyle: "solid",
                borderWidth: "1px",
                borderRadius: "4px",
                padding: "4px",
                boxSizing: "border-box",
              }}
            >
              <Typography variant="caption">
                  {service.title} ({service.from?.format("HH:mm")}-{" "}
                  {service.to?.format("HH:mm")})
                </Typography>
            </Grid2>
          </Tooltip>
          {returnTravelTime > 0 && (
          <Tooltip
            key={index}
            title={
              <Box>
                <Typography variant="caption">
                    Travel Time (Return): {service?.returnTravelTime} mins
                </Typography>
              </Box>
            }
            placement="top"
            arrow
          >
            <Grid2
              key={index}
              sx={{
                position: "absolute",
                top: `${startOffsetReturnTravel * 40}px`,
                height: `${durationReturnTravel * 40}px`,
                left: `${15 + boxLeft}%`,
                width: `${boxWidth - 1}%`,
                backgroundColor: (theme) =>
                  alpha(theme.palette[boxColor].main, 0.2),

                color: "white",
                borderColor: `${boxColor}.main`,
                borderStyle: "solid",
                borderWidth: "1px",
                borderRadius: "4px",
                padding: "4px",
                boxSizing: "border-box",
              }}
            >
            </Grid2>
          </Tooltip>
          )}
        </Fragment>
      );
    });
  };

  const isWorkingHour = (time) => {
    const currentTime = dayjs().set("hour", time.split(":")[0]).set("minute", time.split(":")[1]);
    return workingHours.some((wh) => {
      return currentTime.isBetween(wh.from, wh.to, null, "[]");
    });
  }

  // const renderSuggestedSlots = () => {
  //   const getSuggestedSlots = (freeTimeArray, durationInMinutes) => {
  //     const suggestedSlots = [];
    
  //     freeTimeArray.forEach((timeRange) => {
  //       const startTime = dayjs().hour(timeRange.from.hour()).minute(timeRange.from.minute());
  //       const endTime = dayjs().hour(timeRange.to.hour()).minute(timeRange.to.minute());
    
  //       let currentTime = startTime;
    
  //       while (currentTime.add(durationInMinutes, "minute").isBefore(endTime) || currentTime.add(durationInMinutes, "minute").isSame(endTime)) {
  //         const slotStart = currentTime;
  //         const slotEnd = currentTime.add(durationInMinutes, "minute");
    
  //         suggestedSlots.push({
  //           from: slotStart.format("HH:mm"),
  //           to: slotEnd.format("HH:mm"),
  //         });
    
  //         currentTime = slotEnd;
  //       }
  //     });
    
  //     return suggestedSlots;
  //   };
  // }
  

  return physician && selectedDate ? (
    <Grid2 container size={12} sx={{ paddingX: 2 }}>
      <Typography
        variant="h6"
        gutterBottom
        sx={{ fontWeight: "medium", color: "primary.main" }}
      >
        Scheduling Assistant
      </Typography>
      <Typography>
        Showing availability for {physician?.salutation} {physician?.firstName} {physician?.lastName} on{" "}
        {dayjs(selectedDate).format("DD-MMM-YYYY")}
      </Typography>
      <Grid2 size={12} spacing={2}>
        <Grid2 size={12} key={physician?.id}>
          <Grid2
            sx={{
              paddingTop: 2,
              position: "relative",
              height: "500px",
              overflow: "hidden",
            }}
          >
            <Card variant="outlined" sx={{ height: "100%" }}>
              <Grid2
                sx={{ position: "relative", height: "100%", overflowY: "auto" }}
                ref={gridRef}
              >
                {workingHours && timeSlots() && timeSlots().map((time) => (
                  <Grid2
                    size={12}
                    key={time}
                    sx={{
                      borderBottom: "1px solid #ddd",
                      height: "40px",
                      display: "flex",
                      alignItems: "start",
                      justifyContent: "start",
                      paddingLeft: "10px",
                      backgroundColor: isWorkingHour(time)
                        ? "white"
                        : "grey.100",
                    }}
                  >
                    <Typography variant="caption">{time}</Typography>
                  </Grid2>
                ))}
                {renderAppointments()}
              </Grid2>
            </Card>
          </Grid2>
        </Grid2>
      </Grid2>
    </Grid2>
  ) : (
    <Grid2 container size={12} sx={{ paddingX: 2 }}>
      <Typography variant="h6" gutterBottom>
        Please select a physician and date to view the availability
      </Typography>
    </Grid2>
  );
};

export default SchedulingAssistant;
